import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import tinycolor from 'tinycolor2';
import ArticleCardBase from '../../Base';
import Icon from '../../../../Icon';
import ClapIcon from './ClapIcon';
import { stripParagraphTag } from '../../../../../helpers/text';
import canuseDOM from '../../../../../helpers/canUseDOM';
import gtmPush from '../../../../../helpers/gtmPush';

import './styles.scss';

const socialMediaPageView = media =>
  gtmPush({
    event: 'social',
    socialType: 'social follow',
    socialNetwork: media
  });

const footerLinkOut = ({ footerLink, articleURL }) =>
  gtmPush({
    event: 'decodeFooterExternalLink',
    footerLink,
    articleURL
  });

const PortraitEndCard = ({ data, endCard }) => {
  const location = useLocation();
  const [hasClapped, setHasClapped] = useState(false);
  const [animateClap, setAnimateClap] = useState(false);
  const [isIconVisible, setIsIconVisible] = useState(false);
  const {
    backgroundColor,
    creditInfo,
    paragraphC21,
    footerSection,
    clapIconColor,
    socialIconColor
  } = data;
  const locationParts = location.pathname.substr(1).split('/');
  const articleSlug = locationParts[2];
  const COOKIE_NAME = 'clappedArticles';
  const clappedArticles = canuseDOM ? window.localStorage.getItem(COOKIE_NAME) : '[]';
  let slugArray = JSON.parse(clappedArticles) || [];
  const timeoutRef = useRef();
  const endCardRef = useRef();
  const clapIconColorValue = clapIconColor || 'black';
  const parsedColor = tinycolor(clapIconColorValue);
  const clapIconAltColorValue = parsedColor.isLight() ? 'black' : 'white';

  const displayIconsForLastCardOnly = () => {
    const endCardsDOM = document.getElementsByClassName('portrait-end-card');
    const lastEndCard = endCardsDOM[endCardsDOM.length - 1];

    setIsIconVisible(endCardRef.current === lastEndCard);
  };

  const handleClapButtonClick = isClapped => {
    clearTimeout(timeoutRef.current);

    setAnimateClap(true);

    timeoutRef.current = setTimeout(() => {
      setAnimateClap(false);
    }, 200);

    setHasClapped(isClapped);
    gtmPush({
      event: 'claps',
      clapState: isClapped ? 'clapped' : 'unclapped',
      entrySlug: articleSlug
    });

    if (isClapped) {
      slugArray.push(articleSlug);
    } else {
      slugArray = slugArray.filter(item => item !== articleSlug);
    }
    window.localStorage.setItem(COOKIE_NAME, JSON.stringify(slugArray));
  };

  useEffect(() => {
    displayIconsForLastCardOnly();

    const isArticleClapped = slugArray.find(slug => slug === articleSlug);

    if (isArticleClapped) {
      setHasClapped(true);
    }
  }, []);

  return (
    <ArticleCardBase variation="portrait" accentColor1={backgroundColor} endCard={endCard}>
      <section ref={endCardRef} className="card-container portrait-end-card">
        <div className="portrait-end-card__credits">
          {creditInfo && creditInfo.length
            ? creditInfo.map((credit, index) => (
                <div className="portrait-end-card__credit-item" key={`credit-${index}`}>
                  <div dangerouslySetInnerHTML={{ __html: credit.eyebrow }} />
                  <div dangerouslySetInnerHTML={{ __html: credit.jobTitle }} />
                  <div dangerouslySetInnerHTML={{ __html: credit.personName }} />
                </div>
              ))
            : null}
        </div>
        {paragraphC21 && paragraphC21.length ? (
          <div
            className="portrait-end-card__content"
            dangerouslySetInnerHTML={{ __html: paragraphC21 }}
          />
        ) : null}
        {footerSection && footerSection.length ? (
          <div className="portrait-end-card__footer-links">
            {footerSection.map((link, linkIndex) => {
              const { footerCopy, footerLink, footerLinkCopy, showArrowIcon, arrowIconColor } =
                link;

              return (
                <div key={`footer-link-${linkIndex}`} className="portrait-end-card__footer-link">
                  {footerCopy && (
                    <p dangerouslySetInnerHTML={{ __html: stripParagraphTag(footerCopy) }} />
                  )}
                  {footerLink && (
                    <a
                      target="__blank"
                      onClick={() =>
                        footerLinkOut({ footerLink, articleURL: window.location.href })
                      }
                      href={footerLink}
                      rel="noreferrer">
                      {showArrowIcon ? (
                        <button
                          aria-label={(footerLinkCopy || '').replace(/(<([^>]+)>)/gi, '')}
                          type="button"
                          className="portrait-end-card__link-arrow">
                          <span style={{ backgroundColor: arrowIconColor }} />
                          <span style={{ backgroundColor: arrowIconColor }} />
                        </button>
                      ) : (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: stripParagraphTag(footerLinkCopy || '')
                          }}
                        />
                      )}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
        {isIconVisible && (
          <>
            <div className="portrait-end-card__social-links">
              <p className="portrait-end-card__social-label" style={{ color: clapIconColor }}>
                Claps
              </p>
              <div className="portrait-end-card__social-icons">
                <button
                  title="Clap Article"
                  type="button"
                  onClick={() => handleClapButtonClick(!hasClapped)}
                  className={`portrait-end-card__clap-button ${animateClap ? 'clap-animate' : ''}`}
                  style={{
                    borderColor: clapIconColor,
                    backgroundColor: hasClapped ? clapIconColorValue : ''
                  }}>
                  <ClapIcon
                    theme={{
                      primary: hasClapped ? clapIconColorValue : backgroundColor,
                      secondary: hasClapped ? clapIconAltColorValue : clapIconColorValue,
                      tertiary: hasClapped ? clapIconAltColorValue : clapIconColorValue
                    }}
                  />
                </button>
              </div>
            </div>
            <div className="portrait-end-card__social-links">
              <p className="portrait-end-card__social-label" style={{ color: socialIconColor }}>
                Follow us on
              </p>
              <div className="portrait-end-card__social-icons">
                <a
                  title="Follow us on Twitter"
                  href="https://twitter.com/codeandtheory"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="portrait-end-card__social-link"
                  style={{ borderColor: socialIconColor }}
                  onClick={() => socialMediaPageView('Twitter')}>
                  <Icon name="twitter" fillOverride={socialIconColor} />
                </a>
                <a
                  title="Follow us on Linkedin"
                  href="https://www.linkedin.com/company/code-and-theory"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="portrait-end-card__social-link"
                  style={{ borderColor: socialIconColor }}
                  onClick={() => socialMediaPageView('Linkedin')}>
                  <Icon name="linkedin" fillOverride={socialIconColor} />
                </a>
                <a
                  title="Follow us on Instagram"
                  href="https://www.instagram.com/codeandtheory/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="portrait-end-card__social-link"
                  style={{ borderColor: socialIconColor }}
                  onClick={() => socialMediaPageView('Instagram')}>
                  <Icon name="instagram" fillOverride={socialIconColor} />
                </a>
              </div>
            </div>
          </>
        )}
      </section>
    </ArticleCardBase>
  );
};

export default PortraitEndCard;
